import { Box, Switch, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import SectionActions from "../../../gat-admin/admin-config-companies-settings/section-actions";
import { updateCompanySettings } from "../../../gat-admin/store/gat-admin-slice";

const TRANSPORT_ENABLED = "transport.enabled";

interface TransportEnabledProps {
	locationId: number;
	children?: React.ReactNode;
}

// The location half of the transport flag. Transport needs both the company and
// this location switched on, so the switch is dead until the company has it —
// the same shape the company/location pair takes everywhere else in admin.
// Everything the flag governs (the stops) is nested inside the card and only
// appears once the switch is on, the way Absence Management reads at company
// level, with the save/discard pair closing the card.
const TransportEnabled = ({ locationId, children }: TransportEnabledProps) => {
	const dispatch = useDispatch();

	const companySettings = useSelector(
		(state: any) => state.gatAdminConfiguration.companySettings
	);
	const locationSettings = useSelector(
		(state: any) => state.gatAdminConfiguration.locationSettings
	);
	const updateStatus = useSelector(
		(state: any) => state.gatAdminConfiguration.updateCompanySettings.status
	);
	const getLocationSettingsStatus = useSelector(
		(state: any) => state.gatAdminConfiguration.getLocationSettings.status
	);

	const isCompanyTransportEnabled =
		companySettings[TRANSPORT_ENABLED]?.value ?? false;
	const savedValue = locationSettings[TRANSPORT_ENABLED]?.value ?? false;

	const [isEnabled, setIsEnabled] = useState(savedValue);

	useEffect(() => {
		setIsEnabled(savedValue);
	}, [savedValue]);

	// A location cannot stay on once the company is switched off. Reflect that
	// straight away rather than showing a stale on-switch the API would refuse.
	useEffect(() => {
		if (!isCompanyTransportEnabled) {
			setIsEnabled(false);
		}
	}, [isCompanyTransportEnabled]);

	const handleSave = () => {
		if (!locationId) return;
		dispatch(
			updateCompanySettings([
				{
					code: TRANSPORT_ENABLED,
					entity: "location",
					entityId: locationId,
					type: "System.Boolean",
					value: isEnabled,
				},
			])
		);
	};

	return (
		<Box
			sx={{
				backgroundColor: "#F5F5F5",
				borderRadius: "8px",
				marginTop: "32px",
				marginBottom: "16px",
				padding: "16px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box sx={{ display: "flex", flexDirection: "column" }}>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "16px",
							color: "rgba(0, 0, 0, 0.87)",
						}}
					>
						Transport at this location
					</Typography>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "12px",
							color: "rgba(0, 0, 0, 0.6)",
						}}
					>
						{isCompanyTransportEnabled
							? "Let workers here book a ride to and from their shifts. Add at least one active stop below — until then workers see no transport step."
							: "Transport is switched off for this company. Switch it on in the company's Add-On Features first."}
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<Switch
						checked={isEnabled}
						onChange={(e) => setIsEnabled(e.target.checked)}
						disabled={!isCompanyTransportEnabled}
					/>
				</Box>
			</Box>

			{isEnabled ? children : null}

			<SectionActions
				oldData={{ isEnabled: savedValue }}
				newData={{ isEnabled }}
				resetChanges={() => setIsEnabled(savedValue)}
				handleSave={handleSave}
				status={
					updateStatus === "pending" || getLocationSettingsStatus === "pending"
						? "pending"
						: "fulfilled"
				}
				loading={updateStatus === "pending"}
			/>
		</Box>
	);
};

export default TransportEnabled;
